import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { OutlineLinkButton, PrimaryButton } from '../buttons';
import ChevronDownIcon from '../icons/chevron-down-solid.svg';
import ChevronUpIcon from '../icons/chevron-up-solid.svg';
import { Icon } from 'src/components/widgets/icon';
import MaterialCloseSvg from 'src/shared/material_icons/navigation/close.svg';
export type ThemeNotificationBanner = 'CRITICAL' | 'ERROR' | 'WARNING' | 'SUCCESS' | 'INFO' | 'EXTRA' | 'PROMO' | 'DEPRECATED';
export type ButtonsPosition = 'FOOTER' | 'INLINE';
export type BannerVariant = 'slim' | 'extended';
type ButtonSizeType = 'small' | 'medium' | 'large';

export interface BannerButton {
    label: string;
    outline?: boolean;
    linkButton?: boolean;
    disabled?: boolean;
    dataTrack?: string;
    onClick?: () => void;
    isSubmit?: boolean;
    size?: ButtonSizeType;
    href?: string;
}

const NotificationBannerWrap = styled.div<{
    $buttonsPosition?: ButtonsPosition;
    $themeComponent: 'danger' | 'error' | 'warning' | 'information' | 'success' | 'extra' | 'promo' | 'deprecated';
    $variant?: BannerVariant;
}>`
    display: flex;
    width: 100%;
    box-sizing: border-box;
    background: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].backgroundColor};
    border-radius: 6px;
    border: 1px solid
        ${(props) =>
            props.theme.app.component.notificationBanner[props.$themeComponent].borderColor ??
            `${props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor}40`};
    font-size: 13px;
    color: #263238;
    line-height: 1.5;

    a {
        color: ${(props) => props.theme.app.component.link.color};
        font-weight: 500;
        text-decoration: underline;
    }

    > * {
        box-sizing: border-box;
    }

    *,
    *:before,
    *:after {
        box-sizing: border-box;
    }
`;

const NotificationBannerTitle = styled.strong<{
    $variant?: BannerVariant;
    $themeComponent?: 'danger' | 'error' | 'warning' | 'information' | 'success' | 'extra' | 'promo' | 'deprecated';
}>`
    color: inherit;
    font-size: 13px;
    font-weight: 600;
`;

const NotificationBannerContentWrap = styled.div<{
    $variant?: BannerVariant;
}>`
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    font-size: 13px;
    align-items: center;
    gap: ${(props) => (props.$variant === 'slim' ? '12px' : '16px')};
    flex: 1;
`;

const InnerContainer = styled.div<{
    $variant?: BannerVariant;
}>`
    width: 100%;
    display: flex;
    flex-direction: ${(props) => (props.$variant === 'slim' ? 'row' : 'column')};
    justify-content: ${(props) => (props.$variant === 'slim' ? 'flex-start' : 'left')};
    align-items: ${(props) => (props.$variant === 'slim' ? 'center' : 'flex-start')};
    gap: 5px;
`;

const NotificationBannerContainer = styled.div<{
    $hasButton: boolean;
    $buttonsPosition?: ButtonsPosition;
    $variant?: BannerVariant;
}>`
    width: 100%;
    display: flex;
    align-items: ${(props) => (props.$buttonsPosition === 'FOOTER' ? 'normal' : 'center ')};
    flex-direction: ${(props) => (props.$buttonsPosition === 'FOOTER' ? 'column' : 'row ')};
    gap: ${(props) => (props.$buttonsPosition === 'FOOTER' ? '8px' : '16px')};
    ${(props) =>
        props.$hasButton &&
        `
        justify-content: space-between;
    `}
`;

const NotificationBannerContent = styled.div`
    color: inherit;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5;
    text-wrap: pretty;
    white-space: pre-line;
`;

const NotificationBannerButton = styled(PrimaryButton)<{
    $themeComponent: 'danger' | 'error' | 'warning' | 'information' | 'success' | 'extra' | 'promo' | 'deprecated';
    $variant?: BannerVariant;
}>`
    min-width: auto;
    white-space: nowrap;
    background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    border-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};

    &[disabled] {
        border-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
        background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor} !important;
        opacity: 0.9;
    }
`;

const NotificationBannerOutlineButton = styled(OutlineLinkButton)<{
    $themeComponent: 'danger' | 'error' | 'warning' | 'information' | 'success' | 'extra' | 'promo' | 'deprecated';
    $variant?: BannerVariant;
}>`
    min-width: auto;
    white-space: nowrap;
    border: ${(props) => props.theme.app.component.button.basic.border} !important;
    color: #202223 !important;
    ${(props) => props.$variant === 'slim' && `padding-top: 7px; padding-bottom: 7px;`}

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor} !important;
        color: ${(props) => props.theme.app.component.button.basic.hover.textColor} !important;
    }
`;

const NotificationBannerLink = styled.a<{
    $themeComponent: 'danger' | 'error' | 'warning' | 'information' | 'success' | 'extra' | 'promo' | 'deprecated';
    $variant?: BannerVariant;
}>`
    white-space: nowrap;
    color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    font-size: 13px;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
        opacity: 0.9;
    }

    &:active {
        color: ${(props) => props.theme.app.component.notificationBanner[props.$themeComponent].iconContainerBGColor};
    }
`;

const NotificationBannerButtons = styled.div<{
    $variant?: BannerVariant;
}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${(props) => (props.$variant === 'slim' ? '12px' : '28px')};
    justify-content: flex-end;
    div:last-child {
        margin-right: 0;
    }
`;

const NotificationBannerCloseBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    border: none;
    background: none;
    color: #263238;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`;

const NotificationBannerMinimize = styled.div`
    cursor: pointer;
    width: 12px;
    height: 12px;
    color: rgba(0, 0, 0, 0.3);
    opacity: 0.7;
    flex-shrink: 0;
    margin-left: 8px;
    align-self: flex-start;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 10px;
        height: 10px;
    }

    &:hover {
        opacity: 1;
    }
`;

const MinimizedContent = styled.div`
    color: #333;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const BannerIcon = styled(Icon)`
    color: #263238;
    flex-shrink: 0;
`;

const CloseButtonIcon = styled(Icon)``;

interface NotificationBannerProps {
    title?: ReactNode;
    content: ReactNode;
    type?: ThemeNotificationBanner;
    icon?: ReactNode;
    showCloseButton?: boolean;
    showMinimizeButton?: boolean;
    minimizedText?: ReactNode;
    className?: string;
    onClose?: () => void;
    buttons?: BannerButton[];
    buttonsPosition?: ButtonsPosition;
    variant?: BannerVariant;
    slimButtonSize?: ButtonSizeType;
}

const _NotificationBanner: FC<NotificationBannerProps> = ({
    title,
    content,
    type,
    icon,
    showCloseButton,
    showMinimizeButton,
    minimizedText,
    className,
    onClose,
    buttons,
    buttonsPosition,
    variant = 'extended',
    slimButtonSize = 'small'
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const themeComponent =
        type === 'CRITICAL'
            ? 'danger'
            : type === 'ERROR'
              ? 'error'
              : type === 'WARNING'
                ? 'warning'
                : type === 'SUCCESS'
                  ? 'success'
                  : type === 'INFO'
                    ? 'information'
                    : type === 'EXTRA'
                      ? 'extra'
                      : type === 'PROMO'
                        ? 'promo'
                        : type === 'DEPRECATED'
                          ? 'deprecated'
                          : 'information';

    const getButtonSize = (button: BannerButton): ButtonSizeType => {
        if (button.size) return button.size;
        return variant === 'slim' ? slimButtonSize : 'medium';
    };

    return (
        <NotificationBannerWrap className={className} $buttonsPosition={buttonsPosition} $themeComponent={themeComponent} $variant={variant}>
            <NotificationBannerContentWrap $variant={variant}>
                {icon && <BannerIcon aria-hidden='true'>{icon}</BannerIcon>}
                {isMinimized ? (
                    <MinimizedContent>{minimizedText ?? title ?? content}</MinimizedContent>
                ) : (
                    <InnerContainer $variant={variant}>
                        <NotificationBannerContainer $hasButton={(buttons?.length ?? 0) > 0} $buttonsPosition={buttonsPosition} $variant={variant}>
                            <NotificationBannerContent>
                                {title && <NotificationBannerTitle $variant={variant}>{title}</NotificationBannerTitle>} {content}
                            </NotificationBannerContent>
                            {buttons && buttons.length > 0 && (
                                <NotificationBannerButtons $variant={variant}>
                                    {buttons.map((button, index) => {
                                        return button.linkButton ? (
                                            <NotificationBannerLink
                                                $themeComponent={themeComponent}
                                                $variant={variant}
                                                data-track={button.dataTrack}
                                                key={`Button_${index}`}
                                                href={button.href}
                                                target={button.href ? '_blank' : undefined}
                                                rel={button.href ? 'noopener noreferrer' : undefined}
                                                onClick={button.onClick}
                                            >
                                                {button.label}
                                            </NotificationBannerLink>
                                        ) : button.outline ? (
                                            <NotificationBannerOutlineButton
                                                type={button.isSubmit ? 'submit' : 'button'}
                                                $themeComponent={themeComponent}
                                                $variant={variant}
                                                data-track={button.dataTrack}
                                                $disabled={button.disabled ?? false}
                                                key={`Button_${index}`}
                                                onClick={button.onClick}
                                            >
                                                {button.label}
                                            </NotificationBannerOutlineButton>
                                        ) : (
                                            <NotificationBannerButton
                                                type={button.isSubmit ? 'submit' : 'button'}
                                                $themeComponent={themeComponent}
                                                $variant={variant}
                                                $size={getButtonSize(button)}
                                                data-track={button.dataTrack}
                                                disabled={button.disabled ?? false}
                                                key={`Button_${index}`}
                                                onClick={button.onClick}
                                            >
                                                {button.label}
                                            </NotificationBannerButton>
                                        );
                                    })}
                                </NotificationBannerButtons>
                            )}
                        </NotificationBannerContainer>
                    </InnerContainer>
                )}
                {showMinimizeButton ? (
                    <NotificationBannerMinimize onClick={() => setIsMinimized((prev) => !prev)}>
                        {isMinimized ? <ChevronDownIcon /> : <ChevronUpIcon />}
                    </NotificationBannerMinimize>
                ) : (
                    showCloseButton && (
                        <NotificationBannerCloseBtn onClick={onClose} aria-label='Dismiss'>
                            <CloseButtonIcon aria-hidden='true'>
                                <MaterialCloseSvg />
                            </CloseButtonIcon>
                        </NotificationBannerCloseBtn>
                    )
                )}
            </NotificationBannerContentWrap>
        </NotificationBannerWrap>
    );
};

export const NotificationBanner = styled(_NotificationBanner)``;

export default NotificationBanner;
